import React, { useEffect, useState } from "react";
import { Form, Input, Modal, Col, Button, Descriptions } from "antd";
import {
  LeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  UploadOutlined,
  InfoCircleTwoTone,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getSingleInvoiceRequestData,
  editTaxInvoiceRequest,
  getPartnerInvoiceData,
  getPartnerTaxInvoiceData,
  getSingleTaxRequestData,
} from "../../../appRedux/actions";
import { useNavigate, useParams } from "react-router-dom";
import { royalty_countries_product } from "../../../constants";
import LoadingBar from "../../../components/LoadingBar";
import moment from "moment";
import { getRole, getUserInfo, getIsApprover } from "../../../helpers";
import { useGetIndividualPartnerTaxInvoiceDataQuery } from "../../../appRedux/api/partnerInvoiceApi";
const confirm = Modal.confirm;

const { TextArea } = Input;

const ViewTaxInvoiceTracking = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const [selectedViewItem, setSelectedViewItem] = useState(null);
  const { editTaxRequestData } = useSelector(
    ({ partnerInvoice }) => partnerInvoice,
  );
  const [isAdminApprover, setIsAdminApprover] = useState([]);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserPartner, setIsUserPartner] = useState(false);
  const [isUserLicensor, setIsUserLicensor] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // Get tax_id from URL params instead of props.match
  const taxInvoiceId = params?.tax_id;

  const {
    data: individualTaxData,
    isLoading: isIndividualTaxLoading,
    refetch: refetchTaxData,
  } = useGetIndividualPartnerTaxInvoiceDataQuery(
    taxInvoiceId, // Use tax_id from useParams
    {
      skip: !taxInvoiceId,
    }
  );

  useEffect(() => {
    if (individualTaxData) {
      setSelectedViewItem(individualTaxData);
    }
  }, [individualTaxData]);

  const fetchData = async () => {
    // Use tax_id from useParams instead of props.match
    await dispatch(getSingleTaxRequestData(taxInvoiceId));
    setIsAdminApprover(getIsApprover());
    setIsUserAdmin(() => {
      return getRole() === "ADMIN";
    });
    setIsUserPartner(() => {
      return getRole() === "PARTNER";
    });
    setIsUserLicensor(() => {
      return getRole() === "LICENSOR";
    });
    if (isUserPartner) {
      setIsDisabled(editTaxRequestData.status !== "APPROVED");
    } else {
      setIsDisabled(editTaxRequestData.status !== "PENDING");
    }
  };

  useEffect(() => {
    fetchData();
  }, [taxInvoiceId]);

  const navigateBack = () => {
    navigate('/partner/partner-invoice');
  };

   const renderActionButtons = () => {
  if (!selectedViewItem) return null;

  // Decide whether the current user can act, based on the workflow status.
  // - Partner acts only when the admin has forwarded the document (status: UNDER PROCESS).
  // - Admin acts when the licensor has (re)submitted (status: PENDING) OR when the partner
  //   has requested a revision. A partner revision sets status to "NEEDS REVISION" while
  //   keeping licensor_status as "UNDER PROCESS" (still the admin's turn), whereas an
  //   admin-forwarded revision sets licensor_status to "NEEDS REVISION" (the licensor's
  //   turn). Both states share status === "NEEDS REVISION", so licensor_status is what
  //   distinguishes them and keeps the admin disabled once the revision is with the licensor.
  let isDisabled;
  if (isUserPartner) {
    isDisabled = selectedViewItem.status !== "UNDER PROCESS";
  } else {
    const adminCanAct =
      selectedViewItem.status === "PENDING" ||
      (selectedViewItem.status === "NEEDS REVISION" &&
        selectedViewItem.licensor_status === "UNDER PROCESS");
    isDisabled = !adminCanAct;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      {(isUserAdmin || isUserPartner) && (
        <>
          <Button
            className="gx-btn-success RSDApproveButton"
            icon={<CheckOutlined />}
            onClick={() =>
              showApproveConfirm(
                selectedViewItem.tax_id || "temp-id",
                selectedViewItem.partner_id?.partner_id || 1,
              )
            }
            id={`approve-${selectedViewItem.tax_id || "temp-id"}`}
            disabled={isDisabled}
          >
            {isUserPartner ? "Accept" : "Submit"}
          </Button>

          <Button
            className="gx-btn-yellow RSDRevisionsButton"
            icon={<ExclamationCircleOutlined />}
            onClick={() =>
              showNeedRevisionConfirm(
                selectedViewItem.tax_id || "temp-id",
                selectedViewItem.partner_id?.partner_id || 1,
              )
            }
            id={`revision-${selectedViewItem.tax_id || "temp-id"}`}
            disabled={isDisabled}
          >
            Needs Revision
          </Button>
        </>
      )}
    </div>
  );
}


  const showCommentsForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>Add comments:</span>
        </div>
        <Form.Item
          name="comment"
          className="mb-0"
          rules={[
            {
              required: true,
              message: "Please enter comments",
            },
          ]}
        >
          <TextArea placeholder="Enter comments here" />
        </Form.Item>
      </Form>
    );
  };

  const showApproveConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to submit the report?",
      className: "btn-confirm-green",
      content: showCommentsForm(),
      icon: <CheckCircleOutlined />,
      okText: "Submit",
      cancelText: "Cancel",
      onOk(close) {
        setIsActionLoading(true);
        form.validateFields().then((values) => {
          const approveFormData = new FormData();

          approveFormData.append("comment", values.comment);
          approveFormData.append("partner_id", parseInt(partner_id));
          if (isUserPartner) {
            approveFormData.append("status", "ACCEPTED");
          } else {
            approveFormData.append("status", "APPROVED");
            approveFormData.append("is_approved", true);
          }
          
          dispatch(
            editTaxInvoiceRequest(
              item,
              approveFormData,
              () => {
                setIsActionLoading(false);
                refetchTaxData();
                fetchData();
              },
              "Approved",
            ),
          );
          close();
        }).catch(() => {
          setIsActionLoading(false);
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const showNeedRevisionConfirm = (item, partner_id) => {
    confirm({
      title: "Do you want to request for revisions?",
      className: "btn-confirm-primary",
      content: showCommentsForm(),
      icon: <ExclamationCircleOutlined />,
      okText: "Submit",
      cancelText: "Cancel",
      onOk(close) {
        setIsActionLoading(true);
        form.validateFields().then((values) => {
          const rejectFormData = new FormData();
          rejectFormData.append("status", "NEEDS REVISION");
          rejectFormData.append("comment", values.comment);
          rejectFormData.append("partner_id", parseInt(partner_id));
          
          dispatch(
            editTaxInvoiceRequest(
              item,
              rejectFormData,
              () => {
                setIsActionLoading(false);
                refetchTaxData();
                fetchData();
              },
              "Needs Revision",
            ),
          );
          close();
        }).catch(() => {
          setIsActionLoading(false);
        });
      },
      onCancel() {
        form.resetFields(["comment"]);
      },
    });
  };

  const labelStyle = { width: "200px" };

  // Determine which data to use
  const displayData = selectedViewItem || editTaxRequestData || {};

  return (
    <div>
      <Col span={24}>
        <div
          className="gx-flex-row"
          style={{ justifyContent: "space-between" }}
        >
          <h1 className="title gx-mb-4">
            <LeftOutlined className="mr-5" onClick={navigateBack} />
            Tax Document Information
          </h1>
          {/* Removed duplicate buttons from here - only showing at the bottom */}
        </div>
      </Col>
      <Col span={12}>
        {isIndividualTaxLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
            <LoadingBar size="large" tip="Loading..." />
          </div>
        ) : (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Year" style={labelStyle}>
                {displayData?.year || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Company Name" style={labelStyle}>
                {displayData?.partner_id?.company_name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Licensor Name" style={labelStyle}>
                {displayData?.licensor_id?.first_name} {displayData?.licensor_id?.last_name}
              </Descriptions.Item>
              {(isUserAdmin || isUserPartner) && (
                <Descriptions.Item label="Status" style={labelStyle}>
                  <span
                    style={{
                      color:
                        displayData?.status === "APPROVED" || displayData?.status === "ACCEPTED"
                          ? "green"
                          : displayData?.status === "NEEDS REVISION" || displayData?.status === "INVALID"
                          ? "red"
                          : displayData?.status === "UNDER PROCESS"
                          ? "orange"
                          : "black",
                    }}
                  >
                    {displayData?.status || "N/A"}
                  </span>
                </Descriptions.Item>
              )}
              {isUserLicensor && (
                <Descriptions.Item label="Status" style={labelStyle}>
                  <span
                    style={{
                      color:
                        displayData?.licensor_status === "APPROVED"
                          ? "green"
                          : displayData?.licensor_status === "NEEDS REVISION"
                          ? "red"
                          : "black",
                    }}
                  >
                    {displayData?.licensor_status || "N/A"}
                  </span>
                </Descriptions.Item>
              )}
              {displayData?.comment && (
                <Descriptions.Item label="Comments" style={labelStyle}>
                  {displayData.comment}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Document" style={labelStyle}>
                {displayData?.invoice_file?.map((file, index) => (
                  <React.Fragment key={file.id || index}>
                    <a
                      href={file.document}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      DOC {index + 1}
                    </a>
                    <br />
                  </React.Fragment>
                ))}
              </Descriptions.Item>
            </Descriptions>
             {(isUserAdmin || isUserPartner) && (
          <div className="drawer-action-buttons-container" style={{ marginTop: '24px', backgroundColor: 'transparent' }}>
            {individualTaxData.tax_id && renderActionButtons()}
          </div>
        )}
          </>
        )}
      </Col>
    </div>
  );
};

export default ViewTaxInvoiceTracking;